import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconExternalLink } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useTaskStore } from "../../store/task.store";
import { useDeliveryAdmins } from "../../hooks/useWorkflows";

interface DeliveryNoMethodsContentProps {
  physicianId: string | null;
  practiceId: string | null;
}

export function DeliveryNoMethodsContent({
  physicianId,
  practiceId,
}: DeliveryNoMethodsContentProps) {
  const hasPermission = useHasPermission("report_delivery.manage");
  const selectedTasks = useTaskStore((s) => s.selectedTasks);
  const isAdminEnabled = selectedTasks.some((t) => t.slug === "admin");
  const { data: admins, isLoading: adminsLoading } = useDeliveryAdmins();

  return (
    <Alert
      icon={<IconAlertCircle size={16} />}
      color="orange"
      title="No Delivery Methods Configured"
    >
      <Stack gap="sm">
        <Text size="sm">
          This recipient has no active delivery methods. Reports cannot be sent
          until at least one delivery method is configured.
        </Text>

        {hasPermission && isAdminEnabled && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Configure a delivery method:
            </Text>
            <Group gap="xs" wrap="wrap">
              {physicianId && (
                <Button
                  size="xs"
                  variant="light"
                  color="orange"
                  leftSection={<IconExternalLink size={13} />}
                  component={Link}
                  to={`/admin/physicians?physician=${physicianId}&tab=delivery`}
                >
                  Physician Delivery Settings
                </Button>
              )}
              {practiceId && (
                <Button
                  size="xs"
                  variant="light"
                  color="orange"
                  leftSection={<IconExternalLink size={13} />}
                  component={Link}
                  to={`/admin/practices?practice=${practiceId}&tab=report-delivery`}
                >
                  Practice Delivery Settings
                </Button>
              )}
            </Group>
          </Stack>
        )}

        {hasPermission && !isAdminEnabled && (
          <Text size="sm">
            To configure delivery methods, enable the{" "}
            <Text span fw={600}>
              Admin
            </Text>{" "}
            task using the task switcher in the header, then visit{" "}
            {physicianId && (
              <Anchor
                component={Link}
                to={`/admin/physicians?physician=${physicianId}&tab=delivery`}
                size="sm"
              >
                Physician Delivery Settings
              </Anchor>
            )}
            {physicianId && practiceId && " or "}
            {practiceId && (
              <Anchor
                component={Link}
                to={`/admin/practices?practice=${practiceId}&tab=report-delivery`}
                size="sm"
              >
                Practice Delivery Settings
              </Anchor>
            )}
            .
          </Text>
        )}

        {!hasPermission && (
          <Stack gap="xs">
            <Text size="sm">
              Contact one of the following administrators to configure delivery
              methods:
            </Text>
            {adminsLoading ? (
              <Loader size="xs" />
            ) : admins && admins.length > 0 ? (
              <Stack gap={4}>
                {admins.map((admin) => (
                  <Text key={admin.id} size="sm">
                    <Text span fw={500}>
                      {admin.fullName}
                    </Text>
                    {admin.email && (
                      <Text span c="dimmed">
                        {" "}
                        &mdash;{" "}
                        <Anchor href={`mailto:${admin.email}`} size="sm">
                          {admin.email}
                        </Anchor>
                      </Text>
                    )}
                  </Text>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                No administrators found. Please contact your system
                administrator.
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Alert>
  );
}
